import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MonitorPlay, Loader2, CalendarClock, Video, Eye } from "lucide-react";
import { listDemosForLead } from "../../api/demosApi";
import ScheduleDemoBlock from "./ScheduleDemoBlock";
import MeetEmailModal from "./MeetEmailModal";
import MeetEmailPreviewModal from "./MeetEmailPreviewModal";
import ConfigCallBlock from "./ConfigCallBlock";
import { ActiveDemoCard, ResolvedDemoRow, demoOrigin } from "./DemoCards";
import { useSimulator } from "../../contexts/SimulatorContext";

// ActiveDemoCard/ResolvedDemoRow/demoOrigin viven en DemoCards.jsx (sin cambios de
// comportamiento) para romper el import circular con ConfigCallBlock.jsx. Se re-exportan
// aquí para no romper a quien las importe desde este fichero.
export { ActiveDemoCard, ResolvedDemoRow, demoOrigin };

export default function LeadDemoSection({ lead, onNoCorresponde }) {
  const { t } = useTranslation();
  const { isActive } = useSimulator();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Envío del enlace de la videollamada por email (cuando el lead lo pide por ahí).
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetPreviewOpen, setMeetPreviewOpen] = useState(false);
  const contactId = lead?.contact_id;

  const load = useCallback(async () => {
    if (isActive) {
      // En demo, sin backend: dos demos mock (una activa, una resuelta).
      setDemos([
        { demo_id: "mock-active", status: "agendada", closer: "demo", origin: "televenta", scheduled_at: new Date(Date.now() - 86400000).toISOString(), outcome_note: "" },
        { demo_id: "mock-done", status: "vendida", closer: "demo", origin: "flow", scheduled_at: new Date(Date.now() - 7 * 86400000).toISOString(), outcome_note: "Le encajó el precio." },
      ]);
      setLoading(false);
      return;
    }
    if (!contactId) { setDemos([]); setLoading(false); return; }
    setLoading(true);
    try {
      const list = await listDemosForLead(contactId);
      // Más recientes arriba.
      list.sort((a, b) => new Date(b.scheduled_at || 0) - new Date(a.scheduled_at || 0));
      setDemos(list);
    } catch (_) { setDemos([]); }
    finally { setLoading(false); }
  }, [contactId, isActive]);

  useEffect(() => { load(); }, [load]);

  // Config y demo se separan por origen: cada bloque gestiona su activa y su historial.
  const configs = demos.filter((d) => d.origin === "config");
  const demosOnly = demos.filter((d) => d.origin !== "config");
  // Activa = la más reciente en estado "agendada" (aún sin resultado).
  const active = demosOnly.find((d) => d.status === "agendada");
  const activeConfig = configs.find((d) => d.status === "agendada");
  const resolved = demosOnly.filter((d) => d !== active && d.status !== "reagendada");
  const resolvedConfigs = configs.filter((d) => d !== activeConfig && d.status !== "reagendada");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-indigo-700 font-black text-xs uppercase tracking-wider">
        <MonitorPlay size={16} /> {t("demos.sectionTitle")}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 size={15} className="animate-spin" /> {t("demos.loading")}</div>
      ) : (
        <>
          {active && (
            <ActiveDemoCard
              demo={active}
              onApplied={load}
              onNoCorresponde={onNoCorresponde}
              simulated={isActive}
            />
          )}
          {!active && contactId && (
            <div className="text-sm text-gray-500 flex items-center gap-2">
              <CalendarClock size={15} /> {t("demos.noActive")}
            </div>
          )}
          {resolved.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-bold uppercase text-gray-400">{t("demos.history")}</div>
              {resolved.map((d) => <ResolvedDemoRow key={d.demo_id} demo={d} />)}
            </div>
          )}
          {!active && contactId && lead?.nombre != null && (
            <ScheduleDemoBlock lead={lead} onScheduled={load} simulated={isActive} />
          )}

          {contactId && (
            <ConfigCallBlock
              lead={lead}
              activeConfig={activeConfig}
              resolvedConfigs={resolvedConfigs}
              onChanged={load}
              simulated={isActive}
            />
          )}

          {/* Mandar el enlace de la videollamada por correo: algunos leads lo
              piden por email en vez de por WhatsApp. Con su ojo de vista previa,
              como el resto de envíos de la ficha. */}
          {contactId && !isActive && (
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => setMeetOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition"
              >
                <Video size={15} /> Enviar enlace de videollamada
              </button>
              <button
                type="button"
                onClick={() => setMeetPreviewOpen(true)}
                title="Ver cómo le llega el correo"
                className="p-2 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition"
              >
                <Eye size={15} />
              </button>
            </div>
          )}
        </>
      )}

      <MeetEmailModal
        open={meetOpen}
        onClose={() => setMeetOpen(false)}
        lead={lead}
        scheduledAt={active?.scheduled_at || null}
      />

      {/* El ojo de la ficha enseña el correo de ejemplo con el enlace tipo, para
          poder verlo sin abrir el formulario de envío. */}
      <MeetEmailPreviewModal
        open={meetPreviewOpen}
        onClose={() => setMeetPreviewOpen(false)}
        meetUrl="https://meet.google.com/abc-defg-hij"
        email={lead?.email}
        nombre={lead?.nombre}
        scheduledAt={active?.scheduled_at || null}
        contactId={contactId}
      />
    </div>
  );
}
